import { useRole } from '@/contexts/RoleContext';
import { Eye, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * View As Banner - Visual Indicator for Owner Mode
 *
 * Displays prominent banner at top of screen when platform owner
 * is viewing UI as a different role (View As mode active)
 *
 * Shows:
 * - Eye icon
 * - Current simulated role
 * - Close button to exit View As mode
 */

export function ViewAsBanner() {
  const { isViewAsMode, viewAsRole, setViewAsRole } = useRole();
  const { t } = useTranslation();

  // Only show when View As mode is active
  if (!isViewAsMode || !viewAsRole) return null;

  const roleLabel = t(`roles.${viewAsRole}`);

  return (
    <div
      data-testid="view-as-banner"
      className={cn(
        'sticky top-0 z-[60] w-full',
        'bg-warning-dim border-b-2 border-warning',
        'px-4 py-2',
        'flex items-center justify-center gap-3'
      )}
    >
      <Eye className="h-4 w-4 text-warning" />

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-warning-fg">
          {t('ownerMode.bannerViewingAs', 'Viewing UI as:')}
        </span>
        <span className="text-sm font-bold text-warning-fg">{roleLabel}</span>
      </div>

      <button
        data-testid="view-as-banner-close"
        onClick={() => setViewAsRole(null)}
        className={cn(
          'ml-2 rounded-button p-1.5 transition-colors',
          'text-warning-fg hover:bg-warning-dim',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning'
        )}
        aria-label={t('ownerMode.exitViewAs', 'Exit View As mode')}
        title={t('ownerMode.exitViewAsTooltip', 'Return to platform owner view')}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
